import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { Ban, Search, Trash2, Loader2, AlertTriangle, X, History, Pencil } from 'lucide-react';
import { blacklistApi } from '../../api/blacklist.js';
import { Card, Empty } from '../ui/Card.jsx';
import { Button } from '../ui/Button.jsx';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog.jsx';
import { AddPhoneDialog } from './AddPhoneDialog.jsx';

const PAGE_SIZE = 50;
const ACCEPT = '.csv,.tsv,.txt,.xlsx,.xls';
const CLEAR_WORD = 'DELETE';

/**
 * 禁撥名單分頁（批次外撥頁的第三個 tab）。版面對齊 `blacklist-ui-v12.html` 設計稿。
 *
 * 設計重點（改動前請先看）：
 *   - **分頁 + 搜尋**，不可整份載入（規模可能數萬筆）
 *   - 號碼**原樣純數字顯示**，不加連字號 —— 與 doc ID 一致，搜尋才對得上
 *   - 號碼**不可編輯**（點下去沒反應），只有備註可以；改號碼＝刪除＋重新新增，兩個動作各自留痕
 *   - 備註**行內編輯**（點該格變 input，Enter/blur 存、Esc 取消），hover 浮出淺底與 ✎
 *   - 批次選取：選 >0 才顯示按鈕、**全選只選本頁**（不提供「選取全部 N 筆」）、換頁清空
 *   - 🔴 **刪除一律走 modal，不用 window.confirm**，且**必須列出號碼** ——
 *     刪錯的後果是「那個人又開始被打電話」而且零訊號
 *   - 母帳號限定：批次刪除 / 清空（子帳號勾選欄整個不顯示）。上傳 0922 起改累加（只加不刪）、子帳號也可。
 */
/**
 * ⚠️ 「＋ 新增號碼 / ↑ 上傳 CSV」兩顆按鈕住在 **OutboundPage 的 PageHeader**（照設計稿，
 *    與「外播活動」分頁的「＋ 新建活動」同一個位置；那個 actions 本來就是依 tab 條件式渲染）。
 *    但**狀態不往上搬** —— 整包搬上去會讓另外兩個 tab 也背著禁撥名單的 state。
 *    改用 `useImperativeHandle` 只把兩個動作暴露給父層，其餘一律留在本元件。
 */
export const BlacklistTab = forwardRef(function BlacklistTab({ hostId, isAdmin, canOperate = true }, ref) {
    const { t } = useTranslation('outbound');
    const [items, setItems] = useState([]);
    const [cursors, setCursors] = useState([null]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(null);
    const [q, setQ] = useState('');
    const [searching, setSearching] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sel, setSel] = useState(() => new Set());
    const [adding, setAdding] = useState(false);          // 新增號碼 modal（由 PageHeader 的按鈕打開）
    const [addBusy, setAddBusy] = useState(false);
    const [addError, setAddError] = useState(null);
    const [editing, setEditing] = useState(null);         // { phone, note }
    const [importState, setImportState] = useState(null);
    const [deletions, setDeletions] = useState(null);
    const [confirm, setConfirm] = useState(null);         // { items, kind, busy, error, confirmWord }
    const fileRef = useRef(null);

    useImperativeHandle(ref, () => ({
        openAdd: () => setAdding(true),
        openImport: () => fileRef.current?.click(),
    }), []);

    const load = useCallback(async (pageIdx = 0) => {
        if (!hostId) return;
        setLoading(true); setError(null);
        try {
            const r = await blacklistApi.list(hostId, { limit: PAGE_SIZE, cursor: cursors[pageIdx] || undefined });
            setItems(r.items || []);
            setHasMore(!!r.hasMore);
            setPage(pageIdx);
            setSel(new Set());                             // 換頁清空選取
            if (r.nextCursor) setCursors((c) => { const n = [...c]; n[pageIdx + 1] = r.nextCursor; return n; });
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }, [hostId, cursors]);

    const refreshCount = useCallback(async () => {
        if (!hostId) return;
        try { setTotal((await blacklistApi.count(hostId)).count); } catch { /* 計數失敗不影響主流程 */ }
    }, [hostId]);

    useEffect(() => { setCursors([null]); setQ(''); setSearching(false); load(0); refreshCount(); /* eslint-disable-next-line */ }, [hostId]);

    async function runSearch(e) {
        e?.preventDefault();
        const term = q.trim();
        if (!term) { setSearching(false); setCursors([null]); return load(0); }
        setLoading(true); setError(null);
        try {
            const r = await blacklistApi.search(hostId, term, { limit: 200 });
            setItems(r.items || []); setHasMore(false); setSearching(true); setSel(new Set());
        } catch (e2) { setError(e2.message); }
        finally { setLoading(false); }
    }

    async function doAdd(phone, note) {
        setAddBusy(true); setAddError(null);
        try {
            await blacklistApi.add(hostId, { phone, note });
            setAdding(false);
            await Promise.all([load(page), refreshCount()]);
        } catch (e2) { setAddError(e2.message); }
        finally { setAddBusy(false); }
    }

    async function saveNote(phone, note) {
        setEditing(null);
        const cur = items.find((i) => i.id === phone);
        if (!cur || (cur.note || '') === note) return;
        try {
            await blacklistApi.updateNote(hostId, phone, note);
            setItems((list) => list.map((i) => (i.id === phone ? { ...i, note } : i)));
        } catch (e) { setError(e.message); }
    }

    // ── 刪除一律走 modal（不再有 window.confirm）─────────────────────────────
    const askDeleteOne = (it) => setConfirm({ kind: 'one', items: [{ phone: it.id, note: it.note }] });
    const askDeleteSelected = () => setConfirm({
        kind: 'bulk',
        items: [...sel].map((p) => {
            const it = items.find((i) => i.id === p);
            return { phone: p, note: it?.note };
        }),
    });
    const askClearAll = () => setConfirm({
        kind: 'clear',
        confirmWord: CLEAR_WORD,
        items: items.map((i) => ({ phone: i.id, note: i.note })),   // 目前這頁當樣本，數量以 total 為準
    });

    async function runConfirm() {
        if (!confirm) return;
        setConfirm((c) => ({ ...c, busy: true, error: null }));
        try {
            if (confirm.kind === 'clear') await blacklistApi.clear(hostId);
            else if (confirm.kind === 'bulk') await blacklistApi.bulkDelete(hostId, confirm.items.map((i) => i.phone));
            else await blacklistApi.remove(hostId, confirm.items[0].phone);
            setConfirm(null);
            setCursors([null]);
            await Promise.all([load(0), refreshCount()]);
        } catch (e) {
            setConfirm((c) => (c ? { ...c, busy: false, error: e.message } : null));
        }
    }

    /**
     * 解析檔案 → `[{phone, note, fromNumericCell?}]`。
     * 只有一欄＝純號碼清單；有第二欄＝備註。第一列若不含數字視為標題列。
     *
     * 🔴 **CSV / TSV 一律自己讀文字，不丟給 SheetJS**（2026-09-01 用 pm 的測試檔實測出來的）：
     *   ① SheetJS 會把 `0912345678` 判成數字 → 變 `912345678`，**開頭 0 在解析階段就沒了**，
     *      但那個 0 在 CSV 的文字裡明明存在，是我們自己弄丟的；
     *   ② `XLSX.read(arrayBuffer)` 對 UTF-8 的 CSV 會解碼成 Latin-1（`客訴` → `å®¢è¨´`），
     *      備註整排變亂碼。`file.text()` 才會照 UTF-8 解碼。
     *   → 純文字格式一律走 `parseDelimited()`，值全部是字串，不做任何型別推斷。
     *
     * xlsx / xls 才走 SheetJS，且**必須 `raw: true`** —— `raw:false` 拿到的是 Excel 的格式化
     * 字串（`886911111111` → `"8.86911E+11"`），只留數字會變成 `88691111`，**靜默存進一個
     * 不存在的號碼**。數字型儲存格另外標記 `fromNumericCell`，交後端判定（開頭 0 是否已遺失）。
     */
    async function onPickFile(file) {
        if (!file) return;
        setError(null);
        try {
            const isText = /\.(csv|tsv|txt)$/i.test(file.name || '');
            const rows = isText ? parseDelimited(await file.text()) : parseSheet(await file.arrayBuffer());
            setImportState({ file, rows, diff: null, busy: true });
            const r = await blacklistApi.import(hostId, rows, { sourceFile: file.name, dryRun: true });
            setImportState({ file, rows, diff: r.diff, busy: false });
        } catch (e) { setImportState(null); setError(e.message); }
    }

    async function confirmImport() {
        if (!importState) return;
        setImportState((s) => ({ ...s, busy: true }));
        try {
            await blacklistApi.import(hostId, importState.rows, { sourceFile: importState.file.name });
            setImportState(null);
            setCursors([null]);
            await Promise.all([load(0), refreshCount()]);
        } catch (e) {
            setError(e.message);
            setImportState((s) => (s ? { ...s, busy: false } : null));
        }
    }

    const allOnPage = items.length > 0 && items.every((i) => sel.has(i.id));
    const toggleAllOnPage = () => setSel(allOnPage ? new Set() : new Set(items.map((i) => i.id)));
    const rangeFrom = page * PAGE_SIZE + 1;
    const rangeTo = page * PAGE_SIZE + items.length;

    return (
        <div>
            {/* 標題列：禁撥名單 · 共 N 筆 ＋ 右側動作。
                ⚠️ 設計稿把「＋ 新增號碼 / ↑ 上傳 CSV」畫在頁首那一排，但那個 header 是三個 tab 共用的，
                   放這裡專屬的按鈕會污染另外兩個 tab → 移到本列右側（視覺位置幾乎相同，作用域正確）。 */}
            <div className="flex items-end justify-between gap-3 flex-wrap mb-0.5">
                <div className="flex items-baseline gap-2.5">
                    <h4 className="heading-display text-lg font-semibold text-ink-900 m-0">{t('bl.title')}</h4>
                    <span className="text-[12.5px] text-ink-400">
                        {total == null ? '' : t('bl.countOf', { n: total.toLocaleString() })}
                    </span>
                </div>
                {/* 動作按鈕在 PageHeader（見元件檔頭說明）。這裡只留隱藏的檔案選擇器。 */}
                <input ref={fileRef} type="file" accept={ACCEPT} className="hidden"
                    onChange={(e) => { onPickFile(e.target.files?.[0]); e.target.value = ''; }} />
            </div>
            <p className="text-[12.5px] text-ink-500 mt-0 mb-4">{t('bl.desc')}</p>

            {error && (
                <div className="flex items-start gap-2 rounded-lg border border-danger-500/30 bg-danger-100/40 px-3 py-2 text-sm text-danger-500 mb-3">
                    <AlertTriangle size={15} className="mt-0.5 shrink-0" /> <span>{error}</span>
                </div>
            )}

            {/* 搜尋：獨立卡片、全寬、icon 在框內左側、下方 hint */}
            <Card className="mb-3.5">
                <form onSubmit={runSearch}>
                    <div className="relative">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            aria-label={t('bl.search')}
                            placeholder={t('bl.searchPlaceholder')}
                            className="w-full rounded-lg border border-cream-300 bg-cream-50 py-2.5 pl-8 pr-3 text-[13px] text-ink-900 outline-none focus:border-accent-500"
                        />
                    </div>
                    <div className="text-[11px] text-ink-400 mt-1.5">{t('bl.searchHint')}</div>
                </form>
            </Card>

            {/* 批次動作：選 >0 才出現 */}
            {isAdmin && sel.size > 0 && (
                <div className="flex items-center gap-2 mb-3">
                    <button
                        onClick={askDeleteSelected}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-danger-500/40 bg-white px-3 py-1 text-[11.5px] font-medium text-danger-500 hover:bg-danger-100/30 transition"
                    >
                        <Trash2 size={12} /> {t('bl.deleteSelectedN', { n: sel.size })}
                    </button>
                    <button className="text-[11.5px] text-ink-400 hover:text-ink-700" onClick={() => setSel(new Set())}>
                        {t('bl.clearSel')}
                    </button>
                </div>
            )}

            <Card padded={false}>
                {loading ? (
                    <div className="p-8 text-center text-ink-400"><Loader2 size={18} className="animate-spin inline" /></div>
                ) : items.length === 0 ? (
                    <Empty icon={Ban} title={t('bl.emptyTitle')} description={searching ? t('bl.emptySearch') : t('bl.emptyDesc')} />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-[12.5px]">
                            <thead>
                                <tr className="font-mono text-[9.5px] uppercase tracking-[.09em] text-ink-400">
                                    {isAdmin && (
                                        <th className="w-[30px] text-left px-4 py-2.5 font-medium">
                                            {/* 全選只選本頁 */}
                                            <input type="checkbox" checked={allOnPage} onChange={toggleAllOnPage} aria-label={t('bl.selectPage')} />
                                        </th>
                                    )}
                                    <th className="text-left px-4 py-2.5 font-medium">{t('bl.phone')}</th>
                                    <th className="text-left px-4 py-2.5 font-medium">{t('bl.note')}</th>
                                    <th className="text-left px-4 py-2.5 font-medium w-24">{t('bl.addedVia')}</th>
                                    <th className="text-left px-4 py-2.5 font-medium w-28">{t('bl.addedAt')}</th>
                                    <th className="w-16" />
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it) => {
                                    const picked = sel.has(it.id);
                                    return (
                                        <tr key={it.id} className={`border-t border-cream-100 ${picked ? 'bg-accent-50' : ''}`}>
                                            {isAdmin && (
                                                <td className="px-4 py-2.5">
                                                    <input type="checkbox" checked={picked} aria-label={t('bl.select')} onChange={() => setSel((s) => {
                                                        const n = new Set(s); n.has(it.id) ? n.delete(it.id) : n.add(it.id); return n;
                                                    })} />
                                                </td>
                                            )}
                                            {/* 號碼：原樣純數字、mono；點下去沒反應（不可編輯） */}
                                            <td className="px-4 py-2.5 font-mono tabular-nums text-ink-900">{it.id}</td>
                                            <td className="px-4 py-2.5">
                                                {editing?.phone === it.id ? (
                                                    <>
                                                        <input
                                                            autoFocus
                                                            value={editing.note}
                                                            onChange={(e) => setEditing({ phone: it.id, note: e.target.value })}
                                                            onBlur={() => saveNote(it.id, editing.note)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') saveNote(it.id, editing.note);
                                                                if (e.key === 'Escape') setEditing(null);
                                                            }}
                                                            aria-label={t('bl.editNote')}
                                                            className="w-full rounded-md border border-accent-500 bg-white px-2.5 py-1.5 text-[12.5px] text-ink-900 outline-none"
                                                        />
                                                        <span className="inline-flex items-center gap-1.5 text-[10.5px] text-ink-400 mt-1">
                                                            {t('bl.saveHint')}
                                                        </span>
                                                    </>
                                                ) : !canOperate ? (
                                                    <span className={it.note ? 'text-ink-700' : 'text-ink-300'}>{it.note || '—'}</span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditing({ phone: it.id, note: it.note || '' })}
                                                        className={`group -mx-1.5 -my-0.5 inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-left hover:bg-cream-100 transition ${it.note ? 'text-ink-700' : 'text-ink-300'}`}
                                                    >
                                                        {it.note || t('bl.addNote')}
                                                        <Pencil size={10} className="opacity-0 group-hover:opacity-100 text-ink-400 transition" />
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5">
                                                {it.source === 'import' ? (
                                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-cream-100 px-2.5 py-0.5 text-[10.5px] font-medium text-ink-700">
                                                        <i className="block size-1 rounded-full bg-current" />{t('bl.viaCsv')}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-warn-100 px-2.5 py-0.5 text-[10.5px] font-medium text-warn-500">
                                                        <i className="block size-1 rounded-full bg-current" />{t('bl.viaManual')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5 font-mono tabular-nums text-[11.5px] text-ink-400">{fmtTime(it.addedAt)}</td>
                                            <td className="px-4 py-2.5 text-right">
                                                {canOperate && (
                                                    <button className="text-[11.5px] text-danger-500 hover:underline" onClick={() => askDeleteOne(it)}>
                                                        {t('bl.delete')}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* 分頁：補上「第 A–B 筆，共 N 筆」 */}
            {!searching && items.length > 0 && (
                <div className="flex items-center justify-between gap-3 mt-3">
                    <Button disabled={page === 0 || loading} onClick={() => load(page - 1)}>{t('bl.prev')}</Button>
                    <span className="text-[11.5px] text-ink-400">
                        {t('bl.range', { from: rangeFrom, to: rangeTo, total: total == null ? '—' : total.toLocaleString() })}
                    </span>
                    <Button disabled={!hasMore || loading} onClick={() => load(page + 1)}>{t('bl.next')}</Button>
                </div>
            )}
            {searching && (
                <div className="text-[11.5px] text-ink-400 mt-2.5">{t('bl.searchResult', { n: items.length })}</div>
            )}

            {/* 稽核 + 清空（母帳號） */}
            {isAdmin && (
                <div className="flex items-center gap-2 mt-5">
                    <Button onClick={async () => setDeletions(deletions ? null : (await blacklistApi.deletions(hostId)).items)}>
                        <History size={14} /> {t('bl.deletions')}
                    </Button>
                    <button className="ml-auto text-[11.5px] text-danger-500 hover:underline" onClick={askClearAll}>
                        {t('bl.clear')}
                    </button>
                </div>
            )}
            {deletions && (
                <Card className="mt-3">
                    <div className="text-sm text-ink-600 mb-2">{t('bl.deletionsTitle')}</div>
                    {deletions.length === 0 ? <div className="text-sm text-ink-400">{t('bl.deletionsEmpty')}</div> : (
                        <ul className="text-[12.5px] space-y-1 max-h-64 overflow-auto">
                            {deletions.map((d) => (
                                <li key={d.id} className="text-ink-600">
                                    <span className="font-mono tabular-nums">{d.phone}</span>
                                    <span className="text-ink-300"> · {d.via}</span>
                                    {d.note ? <span className="text-ink-400"> · {d.note}</span> : null}
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            )}

            {/* 新增號碼 —— modal（jacob 要求：popup、且要說明要輸入什麼） */}
            <AddPhoneDialog
                open={adding}
                busy={addBusy}
                error={addError}
                onClose={() => { setAdding(false); setAddError(null); }}
                onSubmit={doAdd}
            />

            {/* 刪除確認 —— 單筆／批次／清空共用同一支 modal（不再有 window.confirm） */}
            <ConfirmDeleteDialog
                open={!!confirm}
                items={confirm?.items || []}
                confirmWord={confirm?.confirmWord || null}
                busy={!!confirm?.busy}
                error={confirm?.error || null}
                onClose={() => setConfirm(null)}
                onConfirm={runConfirm}
            />

            {/* 匯入差異預覽（整份取代前一定要看到「會刪掉什麼」）*/}
            {importState && (
                <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/30 backdrop-blur-sm p-4">
                    <Card className="w-full max-w-lg">
                        <div className="flex items-center justify-between mb-3">
                            <div className="heading-display font-semibold text-ink-900">{t('bl.importPreview')}</div>
                            <button onClick={() => setImportState(null)} className="text-ink-400 hover:text-ink-700"><X size={16} /></button>
                        </div>
                        <div className="text-sm text-ink-500 mb-3 font-mono">{importState.file.name}</div>
                        {!importState.diff ? (
                            <div className="py-6 text-center text-ink-400"><Loader2 size={18} className="animate-spin inline" /></div>
                        ) : (
                            <>
                                <div className="grid grid-cols-3 gap-3 mb-3">
                                    {[['keep', importState.diff.keep, 'border-success-500/35 bg-success-100'],
                                      ['add', importState.diff.add, 'border-cream-200 bg-cream-50'],
                                      ['remove', importState.diff.remove, 'border-danger-500/35 bg-danger-100/50']].map(([k, v, cls]) => (
                                        <div key={k} className={`rounded-lg border px-3.5 py-3 ${cls}`}>
                                            <div className="font-mono text-[9.5px] uppercase tracking-[.12em] text-ink-400">{t(`bl.diff.${k}`)}</div>
                                            <div className="font-mono text-[23px] font-medium mt-1">{v}</div>
                                        </div>
                                    ))}
                                </div>
                                {importState.diff.remove > 0 && (
                                    <div className="mb-3">
                                        <div className="text-xs text-ink-500 mb-1">{t('bl.willRemove')}</div>
                                        <div className="max-h-32 overflow-auto rounded border border-cream-200 p-2 font-mono text-xs text-ink-600 whitespace-pre-line">
                                            {importState.diff.removeSample.join('\n')}
                                            {importState.diff.remove > importState.diff.removeSample.length
                                                ? `\n… +${importState.diff.remove - importState.diff.removeSample.length}` : ''}
                                        </div>
                                    </div>
                                )}
                                {importState.diff.invalid > 0 && (
                                    /* 逐筆列出「哪一筆沒進去、為什麼」。只顯示數字（「12 筆格式無效，已略過」）的話，
                                       匯 5000 筆的人會直接按下一步 —— 而那 12 個人還撥得出去，且永遠不會有人發現。 */
                                    <div className="mb-3">
                                        <div className="text-xs text-warn-500 mb-1">{t('bl.invalidCount', { n: importState.diff.invalid })}</div>
                                        <div className="max-h-32 overflow-auto rounded border border-warn-500/35 bg-warn-100/40 p-2 font-mono text-xs text-ink-600 whitespace-pre-line">
                                            {(importState.diff.invalidSample || []).map((it) =>
                                                `${it.phone}  ${t(`bl.invalidReason.${it.reason}`, { defaultValue: it.reason })}`).join('\n')}
                                            {importState.diff.invalid > (importState.diff.invalidSample || []).length
                                                ? `\n… +${importState.diff.invalid - (importState.diff.invalidSample || []).length}` : ''}
                                        </div>
                                    </div>
                                )}
                                <div className="flex justify-end gap-2">
                                    <Button onClick={() => setImportState(null)}>{t('bl.cancel')}</Button>
                                    <Button variant="accent" disabled={importState.busy} onClick={confirmImport}>
                                        {importState.busy ? <Loader2 size={14} className="animate-spin" /> : null} {t('bl.applyReplace')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </Card>
                </div>
            )}
        </div>
    );
});

/** 加入時間：設計稿是 `08/31 10:24`（mono、要有時分），不是 toLocaleDateString 的 `2026/9/1` */
function fmtTime(ts) {
    const ms = ts?._seconds != null ? ts._seconds * 1000 : (typeof ts === 'number' ? ts : null);
    if (!ms) return '—';
    const d = new Date(ms);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** CSV / TSV：自己讀文字，值一律是字串（不做型別推斷，開頭 0 不會消失） */
function parseDelimited(text) {
    // 去掉 Excel 匯出的 BOM。⚠️ 誠實註記：這行其實是**多餘的防禦** —— 下面每個欄位都會 `.trim()`，
    //   而 JS 的 `trim()` 把 U+FEFF 當空白處理，BOM 本來就會被吃掉（實測突變掉這行，測試全綠）。
    //   留著是因為它讓意圖明確、成本為零；但別以為有測試在守它。
    const src = String(text || '').replace(/^\uFEFF/, '');
    const delim = src.indexOf('\t') >= 0 && src.indexOf(',') < 0 ? '\t' : ',';
    const rows = [];
    let i = 0;
    for (const line of src.split(/\r\n|\r|\n/)) {
        if (!line.trim()) continue;
        const cells = splitCsvLine(line, delim);
        const phone = (cells[0] || '').trim();
        if (!phone) continue;
        if (i === 0 && !/\d/.test(phone)) { i++; continue; }      // 標題列
        rows.push({ phone, note: (cells[1] || '').trim() });
        i++;
    }
    return rows;
}

/** 支援雙引號包住的欄位（備註可能含逗號） */
function splitCsvLine(line, delim) {
    const out = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQ) {
            if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else inQ = false; }
            else cur += ch;
        } else if (ch === '"') inQ = true;
        else if (ch === delim) { out.push(cur); cur = ''; }
        else cur += ch;
    }
    out.push(cur);
    return out;
}

/** xlsx / xls：SheetJS + raw:true，數字型儲存格標記起來交後端判定 */
function parseSheet(buf) {
    const wb = XLSX.read(buf, { type: 'array' });
    const grid = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, blankrows: false, raw: true });
    const rows = [];
    for (let i = 0; i < grid.length; i++) {
        const cells = grid[i] || [];
        const rawCell = cells[0];
        const phone = String(rawCell == null ? '' : rawCell).trim();
        if (!phone) continue;
        if (i === 0 && !/\d/.test(phone)) continue;               // 標題列
        const row = { phone, note: String(cells[1] == null ? '' : cells[1]).trim() };
        // ⚠️ 數字型儲存格的開頭 0 **在檔案裡就已經沒了**（Excel 存的就是 912345678），
        //    前端無從還原 → 標記交後端判定並在預覽裡點名，請使用者改成文字格式重傳。不猜、不補 0。
        if (typeof rawCell === 'number') row.fromNumericCell = true;
        rows.push(row);
    }
    return rows;
}
